import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { formatCurrency, formatNumber, formatPercent, formatFecha } from "../helpers"

// ───────── UI helpers ─────────

const StatCard = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 220px", minWidth: 0,
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
    padding: "16px 20px",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${accent}1a`, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, flexShrink: 0,
      }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
)
StatCard.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

// ───────── Card por sucursal ─────────

const SucursalCard = ({ sucursal, expanded, onToggle, totalVentasGlobal }) => {
  const pct = Number(sucursal.porcentaje_del_total) || 0
  const truncados = (sucursal.terceros_incluidos || 0) < (sucursal.total_terceros || 0)

  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12,
      overflow: "hidden",
    }}>
      {/* Header clickeable */}
      <button
        type="button"
        onClick={onToggle}
        style={{
          all: "unset", display: "block", width: "100%",
          cursor: "pointer", padding: "14px 18px",
          background: expanded ? "#f8fafc" : "#fff",
          transition: "background .15s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: "#dbeafe", color: "#1d4ed8",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, flexShrink: 0,
          }}>
            <i className="mdi mdi-store-outline" />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              {sucursal.sucursal || "(Sin sucursal)"}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {formatNumber(sucursal.total_terceros)} terceros únicos · {formatNumber(sucursal.total_facturas)} facturas
              {truncados && (
                <span style={{ marginLeft: 8, color: "#92400e", background: "#fef3c7", padding: "1px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                  Mostrando top {sucursal.terceros_incluidos}
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 180 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#059669" }}>
              {formatCurrency(sucursal.total_ventas)}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {formatPercent(pct, 2)} del total
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 30, color: "#94a3b8", fontSize: 22 }}>
            <i className={`mdi mdi-chevron-${expanded ? "up" : "down"}`} />
          </div>
        </div>

        {/* Barra de progreso visual */}
        <div style={{ marginTop: 10, background: "#e2e8f0", height: 5, borderRadius: 999, overflow: "hidden" }}>
          <div style={{
            width: `${Math.min(pct, 100)}%`, height: "100%",
            background: "linear-gradient(90deg, #3b82f6, #10b981)",
          }} />
        </div>
      </button>

      {/* Body expandido */}
      {expanded && (
        <div style={{ borderTop: "1px solid #e2e8f0", overflow: "auto" }}>
          {(!sucursal.terceros || sucursal.terceros.length === 0) ? (
            <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              <i className="mdi mdi-account-off-outline me-1" />
              Esta sucursal no tiene terceros en el rango consultado.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>NIT</th>
                  <th style={th}>Nombre</th>
                  <th style={{ ...th, textAlign: "right" }}>Facturas</th>
                  <th style={{ ...th, textAlign: "right" }}>Subtotal</th>
                  <th style={{ ...th, textAlign: "right" }}>Impuestos</th>
                  <th style={{ ...th, textAlign: "right" }}>Total ventas</th>
                  <th style={{ ...th, textAlign: "right" }}>Promedio</th>
                  <th style={{ ...th, textAlign: "right" }}>Participación</th>
                  <th style={th}>Última factura</th>
                </tr>
              </thead>
              <tbody>
                {sucursal.terceros.map((t, i) => (
                  <tr key={`${t.nitreceptor}-${i}`} style={{ background: i % 2 ? "#fafbfc" : "#fff" }}>
                    <td style={td}>{t.rank ?? i + 1}</td>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>{t.nitreceptor}</td>
                    <td style={{ ...td, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.nombrereceptor}>
                      {t.nombrereceptor || "—"}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>{formatNumber(t.total_facturas)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{formatCurrency(t.total_subtotal)}</td>
                    <td style={{ ...td, textAlign: "right", color: "#7c3aed" }}>{formatCurrency(t.total_impuestos)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "#059669" }}>{formatCurrency(t.total_ventas)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{formatCurrency(t.promedio_venta)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 600, color: "#1d4ed8" }}>
                      {formatPercent(t.participacion, 2)}
                    </td>
                    <td style={{ ...td, fontSize: 11, color: "#64748b" }}>
                      {t.ultima_factura ? formatFecha(String(t.ultima_factura).substring(0, 10)) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              {sucursal.terceros.length > 0 && (
                <tfoot>
                  <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                    <td colSpan={3} style={td}>
                      TOTAL ({formatNumber(sucursal.terceros.length)} terceros mostrados)
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {formatNumber(sucursal.terceros.reduce((a, t) => a + (Number(t.total_facturas) || 0), 0))}
                    </td>
                    <td colSpan={2} />
                    <td style={{ ...td, textAlign: "right", color: "#059669" }}>
                      {formatCurrency(sucursal.terceros.reduce((a, t) => a + (Number(t.total_ventas) || 0), 0))}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              )}
            </table>
          )}

          {/* Footer del card con ratio dentro del total global */}
          <div style={{ padding: "10px 18px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", fontSize: 11, color: "#64748b" }}>
            Esta sucursal aporta <strong style={{ color: "#0f172a" }}>{formatPercent(pct, 2)}</strong> del total global
            {totalVentasGlobal > 0 && (
              <> · {formatCurrency(sucursal.total_ventas)} de {formatCurrency(totalVentasGlobal)}</>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
SucursalCard.propTypes = {
  sucursal: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  totalVentasGlobal: PropTypes.number,
}

// ───────── Tab principal ─────────

const PorSucursalTab = ({
  data, loading, error, onConsultar, onRecargar, onExportar, exportando, exportInfo,
  limitPorSucursal, setLimitPorSucursal, listo,
  totalVentasDetallado,
}) => {
  const [busqueda, setBusqueda] = useState("")
  const [expandidas, setExpandidas] = useState(new Set())
  const [orden, setOrden] = useState("ventas") // ventas | nombre | terceros | facturas
  const [todasExpandidas, setTodasExpandidas] = useState(false)

  const sucursalesFiltradas = useMemo(() => {
    const lista = data?.sucursales || []
    let filt = lista
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      filt = lista.filter(s => (s.sucursal || "").toLowerCase().includes(q))
    }
    const sorted = [...filt]
    if (orden === "ventas") {
      sorted.sort((a, b) => (Number(b.total_ventas) || 0) - (Number(a.total_ventas) || 0))
    } else if (orden === "nombre") {
      sorted.sort((a, b) => String(a.sucursal || "").localeCompare(String(b.sucursal || "")))
    } else if (orden === "terceros") {
      sorted.sort((a, b) => (Number(b.total_terceros) || 0) - (Number(a.total_terceros) || 0))
    } else if (orden === "facturas") {
      sorted.sort((a, b) => (Number(b.total_facturas) || 0) - (Number(a.total_facturas) || 0))
    }
    return sorted
  }, [data, busqueda, orden])

  const toggleSucursal = (nombre) => {
    setExpandidas(prev => {
      const next = new Set(prev)
      if (next.has(nombre)) next.delete(nombre)
      else next.add(nombre)
      return next
    })
  }

  const toggleTodas = () => {
    if (todasExpandidas) {
      setExpandidas(new Set())
      setTodasExpandidas(false)
    } else {
      setExpandidas(new Set(sucursalesFiltradas.map(s => s.sucursal)))
      setTodasExpandidas(true)
    }
  }

  // ───── Vista cuando no se ha consultado todavía ─────
  if (!data && !loading && !error) {
    return (
      <div style={{
        background: "linear-gradient(135deg, #eff6ff, #ffffff)",
        border: "1px solid #bfdbfe", borderRadius: 14,
        padding: "40px 30px", textAlign: "center",
      }}>
        <i className="mdi mdi-store-cog-outline" style={{ fontSize: 56, color: "#3b82f6" }} />
        <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
          Análisis de terceros agrupados por sucursal
        </h5>
        <p style={{ color: "#475569", maxWidth: 520, margin: "8px auto 18px", fontSize: 13 }}>
          Te mostramos cada sucursal con sus terceros acumulados (top N por sucursal)
          usando los mismos filtros del análisis principal. La descarga Excel trae
          una hoja por sucursal con TODOS los terceros (sin límite).
        </p>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "end", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <div>
            <div style={labelStyle}>Top N por sucursal</div>
            <select value={limitPorSucursal} onChange={e => setLimitPorSucursal(Number(e.target.value))} style={input}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50 (default)</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
              <option value={1000}>1000 (máx)</option>
            </select>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onConsultar}
            disabled={!listo}
            style={{ height: 36 }}
          >
            <i className="mdi mdi-magnify me-1" />
            Consultar por sucursal
          </button>
        </div>

        {!listo && (
          <small style={{ color: "#94a3b8", display: "block" }}>
            Define primero los filtros de período en el encabezado.
          </small>
        )}
      </div>
    )
  }

  // ───── Loading ─────
  if (loading) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", border: "1px solid #e2e8f0" }}>
        <Spinner color="primary" />
        <div className="mt-2 text-muted">Cargando terceros por sucursal…</div>
        <div className="mt-1" style={{ fontSize: 11, color: "#94a3b8" }}>
          Puede tardar más con rangos grandes — cache 60s por URL repetida.
        </div>
      </div>
    )
  }

  // ───── Error ─────
  if (error) {
    return (
      <div style={{
        padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca",
        color: "#991b1b", borderRadius: 10,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
        <span>{error}</span>
        <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={onRecargar}>
          Reintentar
        </button>
      </div>
    )
  }

  // ───── Datos cargados ─────
  const meta = data.metadata || {}
  // Ventas globales: priorizamos el total del /detallado (fuente oficial
  // del módulo) para que coincida con el KPI del tab Resumen. Caemos al
  // total del propio endpoint por-sucursal si el usuario entró directo
  // a este tab sin haber consultado el análisis principal.
  const totalDetallado = Number(totalVentasDetallado)
  const totalVentasGlobal = isFinite(totalDetallado) && totalDetallado > 0
    ? totalDetallado
    : Number(meta.total_ventas_global) || 0

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* KPIs globales */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard
          icon="mdi-store-outline"
          label="Sucursales"
          value={formatNumber(meta.total_sucursales)}
          accent="#3b82f6"
          sub="Con ventas en el período"
        />
        <StatCard
          icon="mdi-cash-multiple"
          label="Ventas globales"
          value={formatCurrency(totalVentasGlobal)}
          accent="#10b981"
          sub={`${formatNumber(meta.total_facturas_global)} facturas`}
        />
        <StatCard
          icon="mdi-account-group"
          label="Top N por sucursal"
          value={formatNumber(meta.limit_por_sucursal)}
          accent="#8b5cf6"
          sub="Cambia el límite arriba"
        />
        <StatCard
          icon="mdi-database-search-outline"
          label="Filas procesadas"
          value={formatNumber(meta.total_filas_procesadas)}
          accent="#f59e0b"
          sub={data.duration ? `Generado en ${data.duration}` : ""}
        />
      </div>

      {/* Confirmación de exportación */}
      {exportInfo && (
        <div style={{
          background: "#d1fae5", border: "1px solid #a7f3d0",
          color: "#065f46", padding: "10px 14px", borderRadius: 8,
          fontSize: 13, display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="mdi mdi-check-circle" style={{ fontSize: 18 }} />
          <span>
            Descarga lista: {exportInfo.totalSheets ? `${exportInfo.totalSheets} hojas` : ""}
            {exportInfo.totalRows ? ` · ${formatNumber(exportInfo.totalRows)} filas` : ""}
            {exportInfo.filename ? ` · ${exportInfo.filename}` : ""}
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div style={{
        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
        padding: "12px 16px",
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <div style={{ position: "relative", flex: "1 1 260px", minWidth: 220 }}>
          <i className="mdi mdi-magnify" style={{
            position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
            color: "#94a3b8", fontSize: 16,
          }} />
          <input
            type="text"
            placeholder="Buscar sucursal por nombre…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{
              width: "100%", padding: "8px 10px 8px 34px",
              borderRadius: 8, border: "1px solid #cbd5e1",
              fontSize: 13, background: "#fff",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Ordenar por
          </span>
          <select value={orden} onChange={e => setOrden(e.target.value)} style={{
            ...input, height: 32, fontSize: 12, padding: "4px 8px",
          }}>
            <option value="ventas">Ventas (mayor)</option>
            <option value="terceros">Terceros (más)</option>
            <option value="facturas">Facturas (más)</option>
            <option value="nombre">Nombre A-Z</option>
          </select>
        </div>

        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={toggleTodas}>
          <i className={`mdi ${todasExpandidas ? "mdi-collapse-all-outline" : "mdi-expand-all-outline"} me-1`} />
          {todasExpandidas ? "Colapsar todas" : "Expandir todas"}
        </button>

        <div style={{ width: 1, height: 28, background: "#e2e8f0" }} />

        <button type="button" className="btn btn-sm btn-outline-success" onClick={onExportar} disabled={!!exportando}>
          {exportando === "xlsx-sucursal" ? (
            <><Spinner size="sm" className="me-1" /> Generando…</>
          ) : (
            <><i className="mdi mdi-file-excel me-1" /> Descargar Excel (todas las sucursales)</>
          )}
        </button>

        <button type="button" className="btn btn-sm btn-light" onClick={onRecargar} title="Recargar con los filtros actuales">
          <i className="mdi mdi-refresh" />
        </button>
      </div>

      {/* Lista de sucursales */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {sucursalesFiltradas.length === 0 ? (
          <div style={{ background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 14, padding: 40, textAlign: "center" }}>
            <i className="mdi mdi-store-search-outline" style={{ fontSize: 40, color: "#cbd5e1" }} />
            <div style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>
              {busqueda
                ? `No se encontraron sucursales que coincidan con "${busqueda}".`
                : "No hay sucursales en este período."}
            </div>
          </div>
        ) : (
          sucursalesFiltradas.map(s => (
            <SucursalCard
              key={s.sucursal}
              sucursal={s}
              expanded={expandidas.has(s.sucursal)}
              onToggle={() => toggleSucursal(s.sucursal)}
              totalVentasGlobal={totalVentasGlobal}
            />
          ))
        )}
      </div>

      <div style={{ textAlign: "right", fontSize: 11, color: "#94a3b8" }}>
        <i className="mdi mdi-information-outline me-1" />
        {sucursalesFiltradas.length} de {data.sucursales.length} sucursales mostradas
        {data.timestamp && ` · datos al ${new Date(data.timestamp).toLocaleString("es-CO")}`}
      </div>
    </div>
  )
}
PorSucursalTab.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onConsultar: PropTypes.func,
  onRecargar: PropTypes.func,
  onExportar: PropTypes.func,
  exportando: PropTypes.string,
  exportInfo: PropTypes.object,
  limitPorSucursal: PropTypes.number,
  setLimitPorSucursal: PropTypes.func,
  listo: PropTypes.bool,
  totalVentasDetallado: PropTypes.number,
}

const labelStyle = { fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }
const input = { fontSize: 13, padding: "7px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 36, minWidth: 140 }
const th = { background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#475569", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }
const td = { padding: "9px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 13, color: "#0f172a", verticalAlign: "middle" }

export default PorSucursalTab
